import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { ListGroup, Collapse } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { usePermission } from "../hooks/usePermission";
import { isRouteAllowed } from "../config/routePermissions";
import { SIDEBAR_ICONS } from "./icons/sidebarIconMap";

/** Expanded width matches Figma sidebar (312px). */
const SIDEBAR_EXPANDED_PX = 312;
const SIDEBAR_COLLAPSED_PX = 80;

const SIDEBAR_ICON_PX = 24;
const SIDEBAR_ICON_COLOR = "#030F1B";

function SidebarIcon({ iconKey, className = "", style = {} }) {
  const Icon = SIDEBAR_ICONS[iconKey];
  if (!Icon) return null;
  return (
    <Icon
      aria-hidden
      width={SIDEBAR_ICON_PX}
      height={SIDEBAR_ICON_PX}
      className={className}
      style={{
        flexShrink: 0,
        display: "block",
        color: SIDEBAR_ICON_COLOR,
        ...style,
      }}
    />
  );
}

const SIDEBAR_CHEVRON_PX = 20;

function SidebarChevron({ expanded }) {
  const Chevron = SIDEBAR_ICONS.chevronDown;
  return (
    <span className="sidebar-chevron" aria-hidden>
      <Chevron
        width={SIDEBAR_CHEVRON_PX}
        height={SIDEBAR_CHEVRON_PX}
        style={{
          display: "block",
          color: SIDEBAR_ICON_COLOR,
          transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s ease",
        }}
      />
    </span>
  );
}

export default function SideNavbar({
  open = false,
  onClose,
  desktopCollapsed = false,
}) {
  const location = useLocation();
  const { pathname } = useLocation();
  const [openSubmenus, setOpenSubmenus] = useState(() => new Set());
  const isAdminPath = pathname.startsWith("/admin");
  const dashboardPath = isAdminPath ? "/admin/dashboard" : "/dashboard";
  const userType = isAdminPath ? "admin" : "user";
  const [hoveredTooltip, setHoveredTooltip] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0 });
  const [hoveredDropdown, setHoveredDropdown] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0 });
  const [isMouseOverParent, setIsMouseOverParent] = useState(false);
  const [isMouseOverDropdown, setIsMouseOverDropdown] = useState(false);
  const closeDropdownTimer = useRef(null);
  const {t, i18n} = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  // Check if current language is Arabic for RTL support
  const isRTL = i18n.language === 'ar';

  // Enhanced Menu Items Configuration
 const menuItems = [
  {
    title: t("sidebar.dashboard"),
    path: dashboardPath,
    icon: "dashboard",
    forAdmin: true,
    forUser: true,
  },
  {
    title: t("sidebar.platformUsers"),
    path: "/admin/user-list",
    icon: "usersRound",
    forAdmin: true,
  },
  {
    title: t("sidebar.appManagement"),
    path: "/admin/available-apps",
    icon: "panelsTopLeft",
    forAdmin: true,
  },
  {
    title: t("sidebar.orders"),
    path: "/orders",
    icon: "orders",
    forUser: true,
  },
  {
    title: t("sidebar.shipments"),
    path: "/shipments",
    icon: "truck",
    forUser: true,
  },
  {
    title: t("sidebar.inventory"),
    path: "/inventory",
    icon: "inventory",
    forUser: true,
  },
  {
    title: t("sidebar.storesAndBranches"),
    path: "/store&branches-overview",
    icon: "store",
    forUser: true,
  },
  {
    title: t("sidebar.appsManagement"),
    path: "/apps-management",
    icon: "layers",
    forUser: true,
  },
  {
    title: t("sidebar.subscription"),
    path: "/subscription",
    icon: "fileText",
    forUser: true,
  },
  // User Management (Users / Roles) is rendered as a collapsible parent for tenants
  // Configuration parent menu will be rendered separately for user
  {
    title: t("sidebar.systemLogs"),
    path: "/admin/system-logs",
    icon: "fileText",
    forAdmin: true,
  },
  {
    title: t("sidebar.activityLogs"),
    path: "/admin/activity-logs",
    icon: "history",
    forAdmin: true,
  },
  {
    title: t("sidebar.shipments"),
    path: "/admin/shipments",
    icon: "truck",
    forAdmin: true,
  },
];


  // Parent items with subitems for collapsed view
const parentMenuItems = [
  {
    title: t("sidebar.userManagement"),
    icon: "userSquare2",
    forUser: true,
    key: "userManagement",
  },
  {
    title: t("sidebar.catalogManagement"),
    icon: "bookOpen",
    forUser: true,
    key: "catalog",
  },
  {
    title: t("sidebar.expensesAndReports"),
    icon: "chartNoAxesColumnIncreasing",
    forUser: true,
    key: "expenses",
  },
  {
    title: t("sidebar.configuration"),
    icon: "slidersHorizontal",
    forUser: true,
    key: "configuration",
  },
];


  // Submenu items for better organization
  const userManagementSubItems = useMemo(
    () => [
      { path: "/user-management", title: t("sidebar.users"), icon: "userCheck" },
      { path: "/rbac/roles", title: t("sidebar.roles"), icon: "grid2x2Plus" },
    ],
    [t]
  );

  const catalogSubItems = useMemo(
    () => [
      { path: "/category-overview", title: t("sidebar.categories"), icon: "folderTree" },
      { path: "/products", title: t("sidebar.products"), icon: "shoppingBasket" },
      { path: "/catalog", title: t("sidebar.catalog"), icon: "bookMarked" },
    ],
    [t]
  );

  const expensesSubItems = useMemo(
    () => [
      { path: "/sales-reports", title: t("sidebar.allExpenses"), icon: "fileSpreadsheet" },
      { path: "/reports", title: t("sidebar.reports"), icon: "barChart3" },
      { path: "/shipment-reports", title: t("sidebar.shipmentReports"), icon: "fileSpreadsheet" },
    ],
    [t]
  );

  const configurationSubItems = useMemo(
    () => [{ path: "/printer-settings", title: t("sidebar.printers"), icon: "printer" }],
    [t]
  );

  const { can, canAny, loading, bypass } = usePermission();

  const pathAllowedForUserMenu = useCallback(
    (path) => {
      if (userType === "admin") return true;
      if (loading && !bypass) return false;
      return isRouteAllowed(path, can, canAny);
    },
    [userType, can, canAny, loading, bypass]
  );

  const getSubItems = useCallback(
    (key) => {
      let raw = [];
      switch (key) {
        case "userManagement":
          raw = userManagementSubItems;
          break;
        case "catalog":
          raw = catalogSubItems;
          break;
        case "expenses":
          raw = expensesSubItems;
          break;
        case "configuration":
          raw = configurationSubItems;
          break;
        default:
          raw = [];
      }
      if (userType === "admin") return raw;
      return raw.filter((s) => pathAllowedForUserMenu(s.path));
    },
    [
      userType,
      userManagementSubItems,
      catalogSubItems,
      expensesSubItems,
      configurationSubItems,
      pathAllowedForUserMenu,
    ]
  );

  // Helper to determine if desktop
  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 1024;

  const isActive = (path) => location.pathname === path;

  // Check if any subitem of a parent is active
  const isParentActive = (key) => {
    const subItems = getSubItems(key);
    return subItems.some((item) => isActive(item.path));
  };

  const toggleSubmenu = (key) => (e) => {
    if (e.target.closest("a")) return;
    setOpenSubmenus((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Auto-expand submenu(s) that contain the active route
  useEffect(() => {
    setOpenSubmenus((prev) => {
      const next = new Set(prev);
      ["userManagement", "catalog", "expenses", "configuration"].forEach((key) => {
        if (getSubItems(key).some((item) => isActive(item.path))) {
          next.add(key);
        }
      });
      return next;
    });
  }, [pathname, getSubItems]);

  // Remove timer logic and use immediate close
  const handleParentHover = (key, e) => {
    if (isDesktop && desktopCollapsed) {
      const rect = e.currentTarget.getBoundingClientRect();
      setDropdownPos({ top: rect.top });
      setHoveredDropdown(key);
      setIsMouseOverParent(true);
      if (closeDropdownTimer.current) {
        clearTimeout(closeDropdownTimer.current);
        closeDropdownTimer.current = null;
      }
    }
  };

  const handleParentLeave = () => {
    setIsMouseOverParent(false);
    if (closeDropdownTimer.current) {
      clearTimeout(closeDropdownTimer.current);
    }
    closeDropdownTimer.current = setTimeout(() => {
      if (!isMouseOverDropdown && !isMouseOverParent) {
        setHoveredDropdown(null);
      }
    }, 100);
  };

  const handleDropdownEnter = () => {
    setIsMouseOverDropdown(true);
    if (closeDropdownTimer.current) {
      clearTimeout(closeDropdownTimer.current);
      closeDropdownTimer.current = null;
    }
  };

  const handleDropdownLeave = () => {
    setIsMouseOverDropdown(false);
    if (closeDropdownTimer.current) {
      clearTimeout(closeDropdownTimer.current);
    }
    closeDropdownTimer.current = setTimeout(() => {
      if (!isMouseOverParent && !isMouseOverDropdown) {
        setHoveredDropdown(null);
      }
    }, 100);
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (closeDropdownTimer.current) {
        clearTimeout(closeDropdownTimer.current);
      }
    };
  }, []);

  // Close dropdown if mouse leaves the sidebar area (collapsed only)
  const handleSidebarMouseLeave = () => {
    if (isDesktop && desktopCollapsed) {
      setHoveredDropdown(null);
      setIsMouseOverParent(false);
      setIsMouseOverDropdown(false);
      if (closeDropdownTimer.current) {
        clearTimeout(closeDropdownTimer.current);
      }
    }
  };

  const handleMenuItemClick = () => {
    // Close dropdown when clicking a menu item
    setHoveredDropdown(null);
    setIsMouseOverParent(false);
    setIsMouseOverDropdown(false);
    onClose && onClose();
  };

  return (
    <>
      {open && window.innerWidth < 1024 && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.3)",
            zIndex: 1999,
          }}
          onClick={onClose}
        />
      )}
      <div
        className={`sidebar bg-white${open ? " open" : ""}${
          isDesktop && desktopCollapsed ? " collapsed" : ""
        }`}
        style={{
          width: isDesktop
            ? desktopCollapsed
              ? `${SIDEBAR_COLLAPSED_PX}px`
              : `${SIDEBAR_EXPANDED_PX}px`
            : `${SIDEBAR_EXPANDED_PX}px`,
          transition:
            "width 0.3s cubic-bezier(.4,0,.2,1), left 0.3s cubic-bezier(.4,0,.2,1)",
          zIndex: open && window.innerWidth < 1024 ? 2000 : 1040,
          padding: 0,
          position:
            window.innerWidth < 1024 ? "fixed" : isDesktop ? "sticky" : "fixed",
          top: 0,
          left:
            window.innerWidth < 1024
              ? open
                ? 0
                : `-${SIDEBAR_EXPANDED_PX}px`
              : 0,
          height:
            window.innerWidth < 1024
              ? "100vh"
              : isDesktop
                ? "calc(100vh - 60px)"
                : "100%",
          maxHeight:
            window.innerWidth < 1024
              ? "100vh"
              : isDesktop
                ? "calc(100vh - 60px)"
                : "90vh",
          overflowY: "auto",
          boxSizing: "border-box",
          background: "#FFFFFF",
          borderRight: "1px solid rgba(0, 0, 0, 0.15)",
          boxShadow:
            window.innerWidth < 1024 && open
              ? "0 0.125rem 0.25rem rgba(0,0,0,0.12)"
              : "none",
        }}
        onMouseLeave={handleSidebarMouseLeave}
      >
        {/* Cross button for mobile, absolutely positioned at top right */}
        {open && window.innerWidth < 1024 && (
          <button
            onClick={onClose}
            aria-label="Close sidebar"
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              background: "transparent",
              border: "none",
              fontSize: 28,
              color: "#333",
              zIndex: 2100,
              cursor: "pointer",
              width: 48,
              height: 48,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            className="sidebar-close-btn"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
        {/* Spacer to prevent menu overlap with close button on mobile */}
        {open && window.innerWidth < 1024 && (
          <div style={{ height: 48, width: "100%" }} />
        )}
        {/* Tooltip for collapsed sidebar */}
        {isDesktop &&
          desktopCollapsed &&
          hoveredTooltip &&
          !hoveredDropdown && (
            <div
              className="sidebar-tooltip"
              style={{
                position: "fixed",
                left: isRTL ? "auto" : SIDEBAR_COLLAPSED_PX + 12,
                right: isRTL ? SIDEBAR_COLLAPSED_PX + 12 : "auto",
                top: tooltipPos.top,
                background: "rgba(0,0,0,0.9)",
                color: "#fff",
                padding: "6px 12px",
                borderRadius: 4,
                fontSize: 13,
                whiteSpace: "nowrap",
                zIndex: 9999,
                boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                pointerEvents: "none",
                transform: "translateY(-50%)",
              }}
            >
              {hoveredTooltip}
            </div>
          )}

        {/* Invisible bridge between parent and dropdown */}
        {isDesktop && desktopCollapsed && hoveredDropdown && (
          <div
            style={{
              position: "fixed",
              left: isRTL ? "auto" : SIDEBAR_COLLAPSED_PX,
              right: isRTL ? SIDEBAR_COLLAPSED_PX : "auto",
              top: dropdownPos.top,
              width: 12,
              height: 44,
              background: "transparent",
              zIndex: 9999,
              pointerEvents: "all",
            }}
            onMouseEnter={handleDropdownEnter}
            onMouseLeave={handleDropdownLeave}
          />
        )}

        {/* Dropdown for collapsed sidebar */}
        {isDesktop && desktopCollapsed && hoveredDropdown && (
          <div
            className="sidebar-dropdown"
            style={{
              position: "fixed",
              left: isRTL ? "auto" : `${SIDEBAR_COLLAPSED_PX}px`,
              right: isRTL ? `${SIDEBAR_COLLAPSED_PX}px` : "auto",
              top: dropdownPos.top,
              background: "#fff",
              minWidth: "200px",
              borderRadius: 8,
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              zIndex: 10000,
              border: "1px solid #e9ecef",
              overflow: "hidden",
            }}
            onMouseEnter={handleDropdownEnter}
            onMouseLeave={handleDropdownLeave}
          >
            {getSubItems(hoveredDropdown).map((subItem) => (
              <Link
                to={subItem.path}
                key={subItem.path}
                className="text-decoration-none"
                onClick={handleMenuItemClick}
              >
                <div
                  className={`dropdown-item d-flex align-items-center px-3 py-2 ${
                    location.pathname === subItem.path
                      ? "custom-bg text-white"
                      : "text-dark"
                  }`}
                  style={{
                    borderBottom: "1px solid #f8f9fa",
                    transition: "background-color 0.2s",
                    direction: isRTL ? "rtl" : "ltr",
                  }}
                >
                  <span style={{ fontSize: "14px" }}>{subItem.title}</span>
                </div>
              </Link>
            ))}
          </div>
        )}

        <ListGroup variant="flush" className="sidebar-menu-list">
          {/* Desktop collapsed: show main items and parent items as icons only */}
          {isDesktop && desktopCollapsed ? (
            <>
              {userType === "admin"
                ? menuItems
                    .filter((item) => item.forAdmin)
                    .map((item) => (
                      <Link
                        to={item.path}
                        key={item.path}
                        className="text-decoration-none"
                        onClick={onClose}
                        onMouseEnter={() => setHoveredDropdown(null)}
                      >
                        <ListGroup.Item
                          action
                          active={location.pathname === item.path}
                          className={` mb-1 d-flex justify-content-center align-items-center ${
                            location.pathname === item.path
                              ? "custom-bg text-white"
                              : "text-dark"
                          }`}
                          style={{ height: "44px", padding: 0 }}
                          onMouseEnter={(e) => {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            setTooltipPos({ top: rect.top + rect.height / 2 });
                            setHoveredTooltip(item.title);
                          }}
                          onMouseLeave={() => setHoveredTooltip(null)}
                        >
                          <span className="sidebar-icon-wrapper">
                            <SidebarIcon iconKey={item.icon} />
                          </span>
                        </ListGroup.Item>
                      </Link>
                    ))
                : [
                    // Regular menu items for users
                    ...menuItems
                      .filter((item) => item.forUser && pathAllowedForUserMenu(item.path))
                      .map((item) => (
                        <Link
                          to={item.path}
                          key={item.path}
                          className="text-decoration-none"
                          onClick={onClose}
                          onMouseEnter={() => setHoveredDropdown(null)}
                        >
                          <ListGroup.Item
                            action
                            active={location.pathname === item.path}
                            className={` mb-1 d-flex justify-content-center align-items-center ${
                              location.pathname === item.path
                                ? "custom-bg text-white"
                                : "text-dark"
                            }`}
                            style={{ height: "44px", padding: 0 }}
                            onMouseEnter={(e) => {
                              const rect =
                                e.currentTarget.getBoundingClientRect();
                              setTooltipPos({
                                top: rect.top + rect.height / 2,
                              });
                              setHoveredTooltip(item.title);
                            }}
                            onMouseLeave={() => setHoveredTooltip(null)}
                          >
                            <span className="sidebar-icon-wrapper">
                              <SidebarIcon iconKey={item.icon} />
                            </span>
                          </ListGroup.Item>
                        </Link>
                      )),
                    // Parent items with dropdowns
                    ...parentMenuItems
                      .filter((item) => item.forUser && getSubItems(item.key).length > 0)
                      .map((item) => (
                        <div key={item.key}>
                          <ListGroup.Item
                            action
                            active={isParentActive(item.key)}
                            className={` mb-1 d-flex justify-content-center align-items-center ${
                              isParentActive(item.key)
                                ? "custom-bg text-white"
                                : "text-dark"
                            }`}
                            style={{
                              height: "44px",
                              padding: 0,
                              cursor: "pointer",
                            }}
                            onMouseEnter={(e) => handleParentHover(item.key, e)}
                            onMouseLeave={handleParentLeave}
                          >
                            <span className="sidebar-icon-wrapper ms-3 me-3">
                              <SidebarIcon iconKey={item.icon} />
                            </span>
                          </ListGroup.Item>
                        </div>
                      )),
                  ]}
            </>
          ) : (
            // Expanded or mobile/tablet: original sidebar
            <>
              {menuItems.map((item) =>
                (userType === "admin" && item.forAdmin) ||
                (userType !== "admin" &&
                  item.forUser &&
                  pathAllowedForUserMenu(item.path)) ? (
                  <Link
                    to={item.path}
                    key={item.path}
                    className="text-decoration-none"
                    onClick={onClose}
                  >
                    <ListGroup.Item
                      action
                      active={location.pathname === item.path}
                      className={` mb-1 d-flex align-items-center ${
                        location.pathname === item.path
                          ? "custom-bg text-white"
                          : "text-dark"
                      }`}
                    >
                      <span className="sidebar-icon-wrapper ms-2 me-3">
                        <SidebarIcon iconKey={item.icon} />
                      </span>
                      <span className="menu-text">{item.title}</span>
                    </ListGroup.Item>
                  </Link>
                ) : null
              )}

              {userType !== "admin" && (
                <>
                  {/* User Management: Users / Roles */}
                  {getSubItems("userManagement").length > 0 && (
                    <>
                      <ListGroup.Item
                        action
                        onClick={toggleSubmenu("userManagement")}
                        className=" mb-1 d-flex align-items-center justify-content-between sidebar-small-font"
                        style={{ cursor: "pointer", color: "#030F1B" }}
                      >
                        <div className="d-flex align-items-center">
                          <span
                            className="sidebar-icon-wrapper ms-2 me-3"
                            style={{ color: "#030F1B" }}
                          >
                            <SidebarIcon iconKey="userSquare2" />
                          </span>
                          <span
                            className={`sidebar-label${isDesktop ? " desktop-label" : ""}`}
                            style={{ color: "#030F1B" }}
                          >
                            {t("sidebar.userManagement")}
                          </span>
                        </div>
                        <SidebarChevron expanded={openSubmenus.has("userManagement")} />
                      </ListGroup.Item>

                      <Collapse in={openSubmenus.has("userManagement")} mountOnEnter unmountOnExit>
                        <div className="sidebar-submenu">
                          {getSubItems("userManagement").map((subItem) => (
                            <Link
                              to={subItem.path}
                              key={subItem.path}
                              className="text-decoration-none"
                              onClick={(e) => {
                                e.stopPropagation();
                                onClose && onClose();
                              }}
                            >
                              <ListGroup.Item
                                action
                                active={
                                  location.pathname === subItem.path
                                }
                                className={` mb-1 d-flex align-items-center ${
                                  location.pathname === subItem.path
                                    ? "custom-bg text-white"
                                    : "text-dark"
                                }`}
                                style={{ color: "#030F1B" }}
                              >
                                <span
                                  className="sidebar-icon-wrapper ms-2 me-3"
                                  style={{ color: "#030F1B" }}
                                >
                                  <SidebarIcon iconKey={subItem.icon} />
                                </span>
                                <span
                                  className={`sidebar-label${isDesktop ? " desktop-label" : ""}`}
                                  style={{ color: "#030F1B" }}
                                >
                                  {subItem.title}
                                </span>
                              </ListGroup.Item>
                            </Link>
                          ))}
                        </div>
                      </Collapse>
                    </>
                  )}

                  {/* Catalog Management Dropdown */}
                  {getSubItems("catalog").length > 0 && (
                    <>
                  <ListGroup.Item
                    action
                    onClick={toggleSubmenu("catalog")}
                    className=" mb-1 d-flex align-items-center justify-content-between sidebar-small-font"
                    style={{ cursor: "pointer", color: "#030F1B" }}
                  >
                    <div className="d-flex align-items-center">
                      <span
                        className="sidebar-icon-wrapper ms-2 me-3"
                        style={{ color: "#030F1B" }}
                      >
                        <SidebarIcon iconKey="bookOpen" />
                      </span>
                      <span
                        className={`sidebar-label${isDesktop ? " desktop-label" : ""}`}
                        style={{ color: "#030F1B" }}
                      >
                       {t("sidebar.catalogManagement")}
                      </span>
                    </div>
                    <SidebarChevron expanded={openSubmenus.has("catalog")} />
                  </ListGroup.Item>

                  <Collapse in={openSubmenus.has("catalog")} mountOnEnter unmountOnExit>
                    <div className="sidebar-submenu">
                      {getSubItems("catalog").map((subItem) => (
                        <Link
                          to={subItem.path}
                          key={subItem.path}
                          className="text-decoration-none"
                          onClick={(e) => {
                            e.stopPropagation();
                            onClose && onClose();
                          }}
                        >
                          <ListGroup.Item
                            action
                            active={location.pathname === subItem.path}
                            className={` mb-1 d-flex align-items-center ${
                              location.pathname === subItem.path
                                ? "custom-bg text-white"
                                : "text-dark"
                            }`}
                            style={{ color: "#030F1B" }}
                          >
                            <span
                              className="sidebar-icon-wrapper ms-2 me-3"
                              style={{ color: "#030F1B" }}
                            >
                              <SidebarIcon iconKey={subItem.icon} />
                            </span>
                            <span
                              className={`sidebar-label${isDesktop ? " desktop-label" : ""}`}
                              style={{ color: "#030F1B" }}
                            >
                              {subItem.title}
                            </span>
                          </ListGroup.Item>
                        </Link>
                      ))}
                    </div>
                  </Collapse>
                    </>
                  )}

                  {/* Expenses & Reports Dropdown */}
                  {getSubItems("expenses").length > 0 && (
                    <>
                  <ListGroup.Item
                    action
                    onClick={toggleSubmenu("expenses")}
                    className=" mb-1 d-flex align-items-center justify-content-between sidebar-small-font"
                    style={{ cursor: "pointer", color: "#030F1B" }}
                  >
                    <div className="d-flex align-items-center">
                      <span
                        className="sidebar-icon-wrapper ms-2 me-3"
                        style={{ color: "#030F1B" }}
                      >
                        <SidebarIcon iconKey="chartNoAxesColumnIncreasing" />
                      </span>
                      <span
                        className={` sidebar-label${
                          isDesktop ? " desktop-label" : ""
                        }`}
                        style={{ color: "#030F1B" }}
                      >
                        {t("sidebar.expensesAndReports")}
                      </span>
                    </div>
                    <SidebarChevron expanded={openSubmenus.has("expenses")} />
                  </ListGroup.Item>

                  <Collapse in={openSubmenus.has("expenses")} mountOnEnter unmountOnExit>
                    <div className="sidebar-submenu">
                      {getSubItems("expenses").map((subItem) => (
                        <Link
                          to={subItem.path}
                          key={subItem.path}
                          className="text-decoration-none"
                          onClick={(e) => {
                            e.stopPropagation();
                            onClose && onClose();
                          }}
                        >
                          <ListGroup.Item
                            action
                            active={location.pathname === subItem.path}
                            className={` mb-1 d-flex align-items-center sidebar-small-font ${
                              location.pathname === subItem.path
                                ? "custom-bg text-white"
                                : "text-dark"
                            }`}
                            style={{ color: "#030F1B" }}
                          >
                            <span className="sidebar-icon-wrapper ms-2 me-3" style={{ color: "#030F1B" }}>
                              <SidebarIcon iconKey={subItem.icon} />
                            </span>
                            <span
                              className={`sidebar-label${
                                isDesktop ? " desktop-label" : ""
                              }`}
                              style={{ color: "#030F1B" }}
                            >
                              {subItem.title}
                            </span>
                          </ListGroup.Item>
                        </Link>
                      ))}
                    </div>
                  </Collapse>
                    </>
                  )}

                  {/* Configuration Dropdown */}
                  {getSubItems("configuration").length > 0 && (
                    <>
                  <ListGroup.Item
                    action
                    onClick={toggleSubmenu("configuration")}
                    className=" mb-1 d-flex align-items-center justify-content-between sidebar-small-font"
                    style={{ cursor: "pointer", color: "#030F1B" }}
                  >
                    <div className="d-flex align-items-center">
                      <span
                        className="sidebar-icon-wrapper ms-2 me-3"
                        style={{ color: "#030F1B" }}
                      >
                        <SidebarIcon iconKey="slidersHorizontal" />
                      </span>
                      <span
                        className={`sidebar-label${
                          isDesktop ? " desktop-label" : ""
                        }`}
                        style={{ color: "#030F1B" }}
                      >
                        {t("sidebar.configuration")}
                      </span>
                    </div>
                    <SidebarChevron expanded={openSubmenus.has("configuration")} />
                  </ListGroup.Item>

                  <Collapse in={openSubmenus.has("configuration")} mountOnEnter unmountOnExit>
                    <div className="sidebar-submenu">
                      {getSubItems("configuration").map((subItem) => (
                        <Link
                          to={subItem.path}
                          key={subItem.path}
                          className="text-decoration-none"
                          onClick={(e) => {
                            e.stopPropagation();
                            onClose && onClose();
                          }}
                        >
                          <ListGroup.Item
                            action
                            active={location.pathname === subItem.path}
                            className={` mb-1 d-flex align-items-center sidebar-small-font ${
                              location.pathname === subItem.path
                                ? "custom-bg text-white"
                                : "text-dark"
                            }`}
                            style={{ color: "#030F1B" }}
                          >
                            <span className="sidebar-icon-wrapper ms-2 me-3" style={{ color: "#030F1B" }}>
                              <SidebarIcon iconKey={subItem.icon} />
                            </span>
                            <span
                              className={`sidebar-label${
                                isDesktop ? " desktop-label" : ""
                              }`}
                              style={{ color: "#030F1B" }}
                            >
                              {subItem.title}
                            </span>
                          </ListGroup.Item>
                        </Link>
                      ))}
                    </div>
                  </Collapse>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </ListGroup>
        {/* CSS for collapsed sidebar (move to CSS file if desired) */}
        <style>{`
        .sidebar-menu-list.list-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 16px 16px 20px;
          margin-top: 0;
          font-family: Inter, system-ui, -apple-system, "Segoe UI", sans-serif;
        }
        .sidebar {
          transition: width 0.3s ease-in-out, left 0.3s cubic-bezier(.4,0,.2,1);
          min-height: 0;
          box-sizing: border-box;
          overflow-y: auto;
          font-family: Inter, system-ui, -apple-system, "Segoe UI", sans-serif;
        }
        .sidebar .menu-text,
        .sidebar .sidebar-label {
          font-size: 16px !important;
          font-weight: 400 !important;
          line-height: 1.5 !important;
          color: #030F1B !important;
        }
        .sidebar .sidebar-small-font,
        .sidebar .sidebar-small-font * {
          font-size: 16px !important;
        }
        .sidebar-menu-list .mb-1 {
          margin-bottom: 0 !important;
        }

        /* Figma Nav Link row */
        .sidebar .list-group-item {
          border: none !important;
          background: #FFFFFF;
          padding: 8px !important;
          min-height: 40px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          white-space: nowrap;
          color: #030F1B !important;
          box-sizing: border-box;
        }

        .sidebar .list-group-item .sidebar-icon-wrapper {
          width: 24px;
          min-width: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 12px;
        }
        [dir="rtl"] .sidebar .list-group-item .sidebar-icon-wrapper {
          margin-right: 0;
          margin-left: 12px;
        }

        .sidebar .list-group-item .sidebar-icon-wrapper svg {
          display: block;
        }

        .sidebar-chevron {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 20px;
          height: 20px;
          overflow: hidden;
        }

        .sidebar-chevron svg {
          width: 20px;
          height: 20px;
          display: block;
        }

        .collapse {
          transition: height 300ms ease !important;
        }

        .collapsing {
          transition: height 300ms ease !important;
        }

        .sidebar .sidebar-submenu {
          padding: 0 !important;
          margin: 0 !important;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .sidebar:not(.collapsed) .collapse .list-group-item,
        .sidebar:not(.collapsed) .sidebar-submenu .list-group-item {
          padding: 8px 8px 8px 44px !important;
        }
        [dir="rtl"] .sidebar:not(.collapsed) .collapse .list-group-item,
        [dir="rtl"] .sidebar:not(.collapsed) .sidebar-submenu .list-group-item {
          padding: 8px 44px 8px 8px !important;
        }

        .sidebar .sidebar-submenu .list-group-item,
        .sidebar .sidebar-submenu .list-group-item.active {
          border-left: none !important;
        }

        .fa-angle-up, .fa-angle-down {
          transition: transform 0.3s ease;
        }

        .sidebar.collapsed .list-group-item {
          padding: 8px !important;
          justify-content: center;
          border-radius: 12px;
        }

        .sidebar.collapsed .sidebar-icon-wrapper {
          margin-right: 0 !important;
          margin-left: 0 !important;
        }

        .sidebar.collapsed .sidebar-menu-list.list-group {
          padding: 12px 8px 16px;
          align-items: center;
        }

        .sidebar.collapsed .list-group-item.active,
        .sidebar.collapsed .list-group-item.active.custom-bg {
          background: #E3E9EB !important;
          color: #030F1B !important;
        }

        .sidebar .list-group-item,
        .sidebar.collapsed .submenu-dropdown .list-group-item {
          border-left: none !important;
        }

        /* Active: Figma Primary/Light/Light8 */
        .sidebar .list-group-item.active,
        .sidebar .list-group-item.active.text-white,
        .sidebar.collapsed .submenu-dropdown .list-group-item.active {
          background: #E3E9EB !important;
          color: #030F1B !important;
          font-weight: 400 !important;
        }
        .sidebar .list-group-item.active .sidebar-icon-wrapper,
        .sidebar.collapsed .submenu-dropdown .list-group-item.active .sidebar-icon-wrapper {
          color: #030F1B !important;
        }
        .sidebar .list-group-item.active .sidebar-icon-wrapper svg,
        .sidebar.collapsed .submenu-dropdown .list-group-item.active .sidebar-icon-wrapper svg {
          color: #030F1B !important;
        }
        .sidebar .list-group-item:hover,
        .sidebar.collapsed .submenu-dropdown .list-group-item:hover {
          background: #F3F4F6 !important;
        }

        /* Tooltip styles - LTR */
        .sidebar-tooltip::before {
          content: "";
          position: absolute;
          left: -8px;
          top: 50%;
          transform: translateY(-50%);
          border-width: 8px 8px 8px 0;
          border-style: solid;
          border-color: transparent rgba(0,0,0,0.9) transparent transparent;
        }

        /* Tooltip styles - RTL */
        [dir="rtl"] .sidebar-tooltip::before {
          left: auto;
          right: -8px;
          border-width: 8px 0 8px 8px;
          border-color: transparent transparent transparent rgba(0,0,0,0.9);
        }

        /* Dropdown styles */
        .sidebar-dropdown {
          animation: fadeInScale 0.15s ease-out;
        }

        .sidebar-dropdown .dropdown-item {
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .sidebar-dropdown .dropdown-item:hover {
          background-color: #f8f9fa !important;
        }

        .sidebar-dropdown .dropdown-item.custom-bg {
          background: #E3E9EB !important;
          color: #030F1B !important;
        }

        .sidebar-dropdown .dropdown-item:last-child {
          border-bottom: none !important;
        }

        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        /* Arrow indicator for dropdown - LTR */
        .sidebar-dropdown::before {
          content: "";
          position: absolute;
          left: -8px;
          top: 20px;
          border-width: 8px 8px 8px 0;
          border-style: solid;
          border-color: transparent #fff transparent transparent;
          filter: drop-shadow(-1px 0 1px rgba(0,0,0,0.1));
        }

        /* Arrow indicator for dropdown - RTL */
        [dir="rtl"] .sidebar-dropdown::before {
          left: auto;
          right: -8px;
          border-width: 8px 0 8px 8px;
          border-color: transparent transparent transparent #fff;
          filter: drop-shadow(1px 0 1px rgba(0,0,0,0.1));
        }

        /* Enhanced hover area for parent items and dropdown */
        .sidebar.collapsed .parent-hover-area {
          position: relative;
        }

        .sidebar.collapsed .parent-hover-area::after {
          content: "";
          position: absolute;
          top: 0;
          left: 100%;
          width: 200px;
          height: 100%;
          background: transparent;
          z-index: 9998;
          pointer-events: none;
        }

        .sidebar.collapsed .parent-hover-area:hover::after {
          pointer-events: all;
        }

        /* RTL hover area positioning */
        [dir="rtl"] .sidebar.collapsed .parent-hover-area::after {
          left: auto;
          right: 100%;
        }

        .sidebar-close-btn {
          display: flex;
        }
        @media (min-width: 1024px) {
          .sidebar-close-btn {
            display: none !important;
          }
        }
        @media (max-width: 1023.98px) {
          .sidebar {
            position: fixed !important;
            left: 0;
            top: 0;
            z-index: 2000;
            height: 100vh !important;
            max-height: 100vh !important;
            width: ${SIDEBAR_EXPANDED_PX}px !important;
            box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.12) !important;
            border-right: 1px solid rgba(0,0,0,0.15) !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .sidebar:not(.open) {
            left: -${SIDEBAR_EXPANDED_PX}px !important;
            box-shadow: none !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }

        /* RTL support for sidebar positioning */
        [dir="rtl"] .sidebar {
          left: auto;
          right: 0;
        }

        @media (max-width: 1023.98px) {
          [dir="rtl"] .sidebar {
            left: auto !important;
            right: 0 !important;
          }
          [dir="rtl"] .sidebar:not(.open) {
            left: auto !important;
            right: -${SIDEBAR_EXPANDED_PX}px !important;
          }
        }

        /* RTL support for dropdown bridge */
        [dir="rtl"] .sidebar.collapsed .sidebar-dropdown-bridge {
          left: auto;
          right: ${SIDEBAR_COLLAPSED_PX}px;
        }
      `}</style>
      </div>
    </>
  );
}